import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

import { useAccessPermissions, useCurrentUserRoleNames } from "./useAccessPermissions";
import { useGetUserAccessQuery } from "state/rtkQuery/access/accessQueries";
import UnauthorizedAccess from "./UnauthorizedAccess";

// The ECR Ops portal is scoped to operational job functions only — Enterprise
// Admin and Operations Admin, both GLOBAL roles — regardless of what any
// individual permission string would otherwise allow. This is a deliberate,
// explicit allow-list (not derived from auth:*/user:* permissions), replacing
// the previous env-var email allowlist (REACT_APP_SPECIFIC_USER_EMAILS) with
// a check tied to the user's actual RBAC role instead of a list someone has
// to remember to keep in sync by hand.
//
// This is the single canonical copy — Access Management (a section within
// ECR Ops) currently scopes itself to the exact same list and imports this
// rather than keeping its own separate one, so the two can't drift.
export const ECR_OPS_ROLE_NAMES = ["OperationsAdmin", "EnterpriseAdmin"];

export const isEcrOpsRole = (roleNames) =>
  Array.isArray(roleNames) && roleNames.some((name) => ECR_OPS_ROLE_NAMES.includes(name));

// OperationsUser is deliberately NOT in ECR_OPS_ROLE_NAMES/isEcrOpsRole:
// unlike OperationsAdmin/EnterpriseAdmin, it's assigned PER-ORG (in the same
// shape as OrganizationOwner/Leadership — see useEffectiveOrgRole), never as
// a blanket global grant. "Is this person OperationsUser in at least one
// org" is answered separately below, mirroring RequireLeadershipRole's
// isLeadershipInAnyOrg exactly — it's enough to admit them into the (still
// enterprise-wide-navigable) /ecr-ops portal, it does not by itself scope
// what any individual page then shows them.
export const isOperationsUserInAnyOrg = (organizations) =>
  Array.isArray(organizations) && organizations.some((org) => org.role === "OperationsUser");

// Same two roles are also the only ones meant to get a *global*, cross-org
// organization:read/organization:id:update override elsewhere in the app
// (admin org pages, View Requests, the Sync button, etc). Leadership and
// OperationsUser both hold organization:read too, but both are deliberately
// org-scoped like Owner/Approver — see useEffectiveOrgRole's isLeadership/
// isOperationsUser — so neither may satisfy this check.
export const isCrossOrgAdminRole = isEcrOpsRole;

// A narrower allow-list for WRITE actions within /ecr-ops itself (org create/
// edit/complete, announcement create/update/delete/reorder): these are
// inherently cross-org actions with no single org to scope a per-org
// OperationsUser assignment against, so OperationsUser stays excluded here
// even after gaining org-scoped write access elsewhere (e.g. JoinedTab,
// OrgAccessPage) on the specific orgs it's assigned to.
export const ECR_OPS_WRITE_ROLE_NAMES = ["OperationsAdmin", "EnterpriseAdmin"];

export const isEcrOpsWriteRole = (roleNames) =>
  Array.isArray(roleNames) && roleNames.some((name) => ECR_OPS_WRITE_ROLE_NAMES.includes(name));

const RequireEcrOpsRole = () => {
  const location = useLocation();
  const authToken = useSelector((state) => state.auth?.authToken);
  const email = useSelector((state) => state.auth?.userAccount?.username);
  const { isLoading } = useAccessPermissions();
  const roleNames = useCurrentUserRoleNames();
  const { data: orgAccessData, isLoading: isLoadingOrgAccess } = useGetUserAccessQuery(
    { email },
    { skip: !email }
  );

  if (!authToken) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (isLoading || isLoadingOrgAccess || roleNames === null) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!isEcrOpsRole(roleNames) && !isOperationsUserInAnyOrg(orgAccessData?.organization)) {
    return (
      <UnauthorizedAccess message="This section is reserved for the ECR Ops team. Please return to the home dashboard." />
    );
  }

  return <Outlet />;
};

export default RequireEcrOpsRole;
